import Vue from 'vue'
import Vuex from 'vuex'
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead
} from '@/api/notification'
import { userApi } from '@/api/user'
import { goodsApi } from '@/api/goods'
import { scoreshopApi } from '@/api/scoreshop'
import { resetAuthExpired } from '@/api/request'
import i18n from '@/i18n'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    // 购物车
    cart: [],
    // 购物车弹窗
    cartModalVisible: false,
    // 登录/注册弹窗
    loginModalVisible: false,
    registerModalVisible: false,
    forgotPasswordModalVisible: false,
    accountModalVisible: false,
    accountModalTab: 'overview',
    // 用户信息
    user: null,
    // 用户通知
    notifications: [],
    notificationsLoading: false,
    // 搜索关键词
    searchKeyword: '',
    // 加载状态
    loading: false,
    // 提示信息
    toast: {
      visible: false,
      message: '',
      type: 'info'
    },
    // 语言
    language: localStorage.getItem('language') || 'zh',
    // 接口订单列表
    apiOrders: []
  },
  getters: {
    // 购物车商品列表（已登录时映射后端字段，未登录时返回本地购物车）
    cartItems(state) {
      if (state.user && state.cart.length && state.cart[0].goods_id !== undefined) {
        return state.cart.map(item => ({
          id: item.id,
          goodsId: item.goods_id,
          brandId: item.brand_id || '',
          nameCn: item.title || '',
          name: item.title_en || '',
          brandCn: item.brandname || '',
          brand: item.brandname_en || '',
          image: item.image || '',
          price: item.guigejson && item.guigejson.price ? item.guigejson.price : (item.price || 0),
          originalPrice: item.guigejson && item.guigejson.original_price ? item.guigejson.original_price : '',
          spec: item.guigejson
            ? (state.language === 'en' ? item.guigejson.guigename_en : item.guigejson.guigename)
            : '',
          quantity: item.counts || 1,
          cartId: item.id
        }))
      }
      return state.cart
    },
    // 购物车商品数量
    cartCount(state, getters) {
      return getters.cartItems.reduce((total, item) => total + item.quantity, 0)
    },
    // 购物车总价
    cartTotal(state, getters) {
      return getters.cartItems.reduce((total, item) => {
        const price = parseFloat(String(item.price).replace(/,/g, '')) || 0
        return total + price * item.quantity
      }, 0)
    },
    cartModalVisible(state) {
      return state.cartModalVisible
    },
    loginModalVisible(state) {
      return state.loginModalVisible
    },
    registerModalVisible(state) {
      return state.registerModalVisible
    },
    forgotPasswordModalVisible(state) {
      return state.forgotPasswordModalVisible
    },
    accountModalVisible(state) {
      return state.accountModalVisible
    },
    accountModalTab(state) {
      return state.accountModalTab
    },
    // 是否已登录
    isLoggedIn(state) {
      return !!state.user
    },
    userNotifications(state) {
      return state.notifications
    },
    unreadNotificationCount(state) {
      return state.notifications.filter(n => !n.read).length
    },
    notificationsLoading(state) {
      return state.notificationsLoading
    }
  },
  mutations: {
    // 添加商品到购物车
    ADD_TO_CART(state, product) {
      const addQty = product.quantity || product.qty || 1
      const existing = state.cart.find(item => item.id === product.id)
      if (existing) {
        existing.quantity += addQty
      } else {
        state.cart.push({
          ...product,
          quantity: product.quantity || product.qty || 1
        })
      }
      // 保存到本地存储
      localStorage.setItem('cart', JSON.stringify(state.cart))
    },
    // 从购物车移除商品
    REMOVE_FROM_CART(state, productId) {
      state.cart = state.cart.filter(item => item.id !== productId)
      localStorage.setItem('cart', JSON.stringify(state.cart))
    },
    // 更新商品数量
    UPDATE_CART_ITEM(state, { id, quantity }) {
      const item = state.cart.find(item => item.id === id)
      if (item) {
        item.quantity = quantity
        localStorage.setItem('cart', JSON.stringify(state.cart))
      }
    },
    // 清空购物车
    CLEAR_CART(state) {
      state.cart = []
      localStorage.setItem('cart', JSON.stringify(state.cart))
    },
    // 设置购物车（后端返回数据）
    SET_CART(state, cart) {
      state.cart = Array.isArray(cart) ? cart : []
    },
    // 从本地存储加载购物车
    LOAD_CART(state) {
      const cart = localStorage.getItem('cart')
      if (cart) {
        state.cart = JSON.parse(cart)
      }
    },
    // 设置用户信息
    SET_USER(state, user) {
      state.user = user
      if (user) {
        localStorage.setItem('user', JSON.stringify(user))
      } else {
        localStorage.removeItem('user')
      }
    },
    LOAD_USER(state) {
      const user = localStorage.getItem('user')
      if (user) {
        state.user = JSON.parse(user)
      }
    },
    // 设置搜索关键词
    SET_SEARCH_KEYWORD(state, keyword) {
      state.searchKeyword = keyword
    },
    // 设置加载状态
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    // 显示提示
    SHOW_TOAST(state, { message, type = 'info' }) {
      state.toast = {
        visible: true,
        message,
        type
      }
    },
    // 隐藏提示
    HIDE_TOAST(state) {
      state.toast.visible = false
    },
    SET_CART_MODAL_VISIBLE(state, visible) {
      state.cartModalVisible = visible
    },
    SET_LOGIN_MODAL_VISIBLE(state, visible) {
      state.loginModalVisible = visible
    },
    SET_REGISTER_MODAL_VISIBLE(state, visible) {
      state.registerModalVisible = visible
    },
    SET_FORGOT_PASSWORD_MODAL_VISIBLE(state, visible) {
      state.forgotPasswordModalVisible = visible
    },
    SET_ACCOUNT_MODAL_VISIBLE(state, visible) {
      state.accountModalVisible = visible
    },
    SET_ACCOUNT_MODAL_TAB(state, tab) {
      state.accountModalTab = tab
    },
    SET_LANGUAGE(state, lang) {
      state.language = lang
      localStorage.setItem('language', lang)
    },
    SET_NOTIFICATIONS(state, notifications) {
      state.notifications = notifications
    },
    SET_NOTIFICATIONS_LOADING(state, loading) {
      state.notificationsLoading = loading
    },
    MARK_NOTIFICATION_READ(state, id) {
      const item = state.notifications.find(n => n.id === id)
      if (item) {
        item.read = true
      }
    },
    MARK_ALL_NOTIFICATIONS_READ(state) {
      state.notifications = state.notifications.map(n => ({ ...n, read: true }))
    },
    CLEAR_NOTIFICATIONS(state) {
      state.notifications = []
      state.notificationsLoading = false
    },
    ADD_ORDER(state, order) {
      if (!state.user) return
      const orders = state.user.orders || []
      state.user = {
        ...state.user,
        orders: [order, ...orders]
      }
      localStorage.setItem('user', JSON.stringify(state.user))
    },
    UPDATE_ORDER_STATUS(state, { orderId, status }) {
      if (!state.user || !state.user.orders) return
      const orders = state.user.orders.map(order => {
        if (order.id === orderId) {
          return { ...order, status }
        }
        return order
      })
      state.user = { ...state.user, orders }
      localStorage.setItem('user', JSON.stringify(state.user))
    },
    SET_USER_ADDRESSES(state, addresses) {
      if (!state.user) return
      state.user = {
        ...state.user,
        addresses
      }
      localStorage.setItem('user', JSON.stringify(state.user))
    },
    SET_API_ORDERS(state, orders) {
      state.apiOrders = orders
    }
  },
  actions: {
    // 加载购物车（已登录走接口，未登录读本地）
    async loadCart({ commit, state }) {
      if (!state.user) {
        commit('LOAD_CART')
        return
      }
      try {
        const res = await goodsApi.getCartList()
        if (res.code === 1 && Array.isArray(res.data)) {
          commit('SET_CART', res.data)
        } else {
          commit('LOAD_CART')
        }
      } catch {
        commit('LOAD_CART')
      }
    },
    // 添加到购物车
    async addToCart({ commit, dispatch, state }, product) {
      if (!state.user) {
        commit('ADD_TO_CART', product)
        commit('SHOW_TOAST', {
          message: `${product.nameCn} 已加入購物車`,
          type: 'success'
        })
        return
      }
      try {
        const res = await goodsApi.addCart({
          goods_id: product.id,
          counts: product.quantity || product.qty || 1,
          skuindex: product.skuindex !== undefined && product.skuindex !== '' ? product.skuindex : 0
        })
        if (res.code === 1) {
          commit('SHOW_TOAST', {
            message: `${product.nameCn} 已加入購物車`,
            type: 'success'
          })
          dispatch('loadCart')
        } else {
          commit('SHOW_TOAST', { message: res.msg || '加入購物車失敗', type: 'error' })
        }
      } catch {
        commit('SHOW_TOAST', { message: '加入購物車失敗', type: 'error' })
      }
    },
    // 从购物车移除
    async removeFromCart({ commit, dispatch, state }, productId) {
      if (!state.user) {
        commit('REMOVE_FROM_CART', productId)
        return
      }
      try {
        const res = await goodsApi.deleteCart({ id: productId })
        if (res.code === 1) {
          dispatch('loadCart')
        } else {
          commit('SHOW_TOAST', { message: res.msg || '刪除失敗', type: 'error' })
        }
      } catch {
        commit('SHOW_TOAST', { message: '刪除失敗', type: 'error' })
      }
    },
    // 更新数量
    async updateCartItem({ commit, dispatch, state }, payload) {
      if (!state.user) {
        commit('UPDATE_CART_ITEM', payload)
        return { success: true }
      }
      try {
        const res = await goodsApi.editCounts({
          id: payload.cartId || payload.id,
          counts: payload.quantity
        })
        if (res.code === 1) {
          await dispatch('loadCart')
          return { success: true }
        } else {
          // 库存不足或其他错误，重新加载购物车恢复原数量
          await dispatch('loadCart')
          commit('SHOW_TOAST', { message: res.msg || '更新數量失敗', type: 'error' })
          return { success: false, message: res.msg }
        }
      } catch (error) {
        // 网络错误，重新加载购物车恢复原数量
        await dispatch('loadCart')
        commit('SHOW_TOAST', { message: '更新數量失敗', type: 'error' })
        return { success: false, message: '更新數量失敗' }
      }
    },
    // 清空购物车
    clearCart({ commit }) {
      commit('CLEAR_CART')
    },
    // 显示提示
    showToast({ commit }, payload) {
      commit('SHOW_TOAST', payload)
      setTimeout(() => {
        commit('HIDE_TOAST')
      }, 3000)
    },
    openCartModal({ commit }) {
      commit('SET_CART_MODAL_VISIBLE', true)
    },
    closeCartModal({ commit }) {
      commit('SET_CART_MODAL_VISIBLE', false)
    },
    openLoginModal({ commit }) {
      commit('SET_LOGIN_MODAL_VISIBLE', true)
    },
    closeLoginModal({ commit }) {
      commit('SET_LOGIN_MODAL_VISIBLE', false)
    },
    openRegisterModal({ commit }) {
      commit('SET_REGISTER_MODAL_VISIBLE', true)
    },
    closeRegisterModal({ commit }) {
      commit('SET_REGISTER_MODAL_VISIBLE', false)
    },
    openForgotPasswordModal({ commit }) {
      commit('SET_FORGOT_PASSWORD_MODAL_VISIBLE', true)
    },
    closeForgotPasswordModal({ commit }) {
      commit('SET_FORGOT_PASSWORD_MODAL_VISIBLE', false)
    },
    openAccountModal({ commit }, tab = 'overview') {
      commit('SET_ACCOUNT_MODAL_TAB', tab)
      commit('SET_ACCOUNT_MODAL_VISIBLE', true)
    },
    closeAccountModal({ commit }) {
      commit('SET_ACCOUNT_MODAL_VISIBLE', false)
    },
    async loadNotifications({ commit, state }) {
      if (!state.user || !state.user.email) {
        commit('CLEAR_NOTIFICATIONS')
        return
      }

      commit('SET_NOTIFICATIONS_LOADING', true)
      try {
        const list = await fetchNotifications(state.user.email)
        commit('SET_NOTIFICATIONS', list)
      } catch {
        commit('SET_NOTIFICATIONS', [])
      } finally {
        commit('SET_NOTIFICATIONS_LOADING', false)
      }
    },
    async markNotificationRead({ commit, state, dispatch }, id) {
      if (!state.user || !state.user.email) return

      commit('MARK_NOTIFICATION_READ', id)
      try {
        await markNotificationAsRead(state.user.email, id)
      } catch {
        dispatch('loadNotifications')
      }
    },
    async markAllNotificationsRead({ commit, state, dispatch }) {
      if (!state.user || !state.user.email) return

      const ids = state.notifications.map(n => n.id)
      commit('MARK_ALL_NOTIFICATIONS_READ')
      try {
        await markAllNotificationsAsRead(state.user.email, ids)
      } catch {
        dispatch('loadNotifications')
      }
    },
    async login({ commit, dispatch }, { email, password }) {
      let res
      try {
        res = await userApi.login({ account: email, password })
      } catch {
        dispatch('showToast', { message: i18n.t('toast.network_error'), type: 'error' })
        return false
      }
      if (res.code === 1 && res.data) {
        const user = { ...res.data }
        commit('SET_USER', user)
        commit('SET_LOGIN_MODAL_VISIBLE', false)
        resetAuthExpired()
        dispatch('showToast', { message: i18n.t('toast.login_success'), type: 'success' })
        dispatch('loadNotifications')
        return true
      }
      dispatch('showToast', { message: res.msg || i18n.t('toast.login_failed'), type: 'error' })
      return false
    },
    async register({ commit, dispatch }, form) {
      const genderMap = { mr: 1, mrs: 2, ms: 3 }
      const apiParams = {
        type: form.type || 1,
        gender: genderMap[form.salutation] || 1,
        surname: '',
        name: form.name || '',
        email: form.email || '',
        address: form.address || '',
        areano: form.phoneRegion || '',
        phone: form.phone || '',
        yzm: form.captcha || '',
        userpwd: form.password || '',
        weixin: form.weixin || '',
      }
      if (form.birthYear && form.birthMonth && form.birthDay) {
        apiParams.birthday = `${form.birthYear}-${String(form.birthMonth).padStart(2, '0')}-${String(form.birthDay).padStart(2, '0')}`
      }
      if (form.language) {
        apiParams.language = form.language
      }
      let res
      try {
        res = await userApi.register(apiParams)
      } catch {
        dispatch('showToast', { message: i18n.t('toast.network_error'), type: 'error' })
        return false
      }
      if (res.code === 1 && res.data) {
        const user = { ...res.data }
        commit('SET_USER', user)
        commit('SET_REGISTER_MODAL_VISIBLE', false)
        resetAuthExpired()
        dispatch('showToast', { message: i18n.t('toast.register_success'), type: 'success' })
        dispatch('loadNotifications')
        return true
      }
      dispatch('showToast', { message: res.msg || i18n.t('toast.register_failed'), type: 'error' })
      return false
    },
    async updateProfile({ commit, dispatch, state }, profileData) {
      if (!state.user) return false

      // 严格按接口文档参数传递: gender, name, phone, address, userpwd(可选), birthday(可选), language(可选)
      const apiParams = {
        gender: profileData.gender,
        name: profileData.name,
        phone: profileData.phone,
        address: profileData.address,
        weixin: profileData.weixin || '',
      }
      if (profileData.userpwd) {
        apiParams.userpwd = profileData.userpwd
      }
      if (profileData.birthday) {
        apiParams.birthday = profileData.birthday
      }
      if (profileData.language) {
        apiParams.language = profileData.language
      }
      if (profileData.weixin) {
        apiParams.weixin = profileData.weixin
      }
      const res = await userApi.updateProfile(apiParams)
      if (res.code === 1) {
        // 更新本地用户数据
        const genderReverseMap = { 1: 'mr', 2: 'mrs', 3: 'ms' }
        const updated = {
          ...state.user,
          gender: apiParams.gender,
          salutation: genderReverseMap[apiParams.gender] || state.user.salutation,
          name: apiParams.name,
          phone: apiParams.phone,
          address: apiParams.address,
          weixin: apiParams.weixin || '',
        }
        if (apiParams.birthday) {
          updated.birthday = apiParams.birthday
          const parts = apiParams.birthday.split('-')
          if (parts.length === 3) {
            updated.birthYear = parseInt(parts[0], 10)
            updated.birthMonth = parseInt(parts[1], 10)
            updated.birthDay = parseInt(parts[2], 10)
          }
        }
        if (apiParams.language) {
          updated.language = apiParams.language
        }
        if (apiParams.userpwd) {
          // 密码已修改，无需额外操作
        }
        commit('SET_USER', updated)
        dispatch('showToast', { message: '帳戶資料已保存', type: 'success' })
        return true
      }
      dispatch('showToast', { message: res.msg || '保存失敗', type: 'error' })
      return false
    },
    // 下单支付（严格按接口文档传参）
    async placeOrder({ commit, dispatch, state }, payload) {
      if (!state.user) {
        dispatch('openLoginModal')
        return null
      }

      const cartids = payload.cartids || ''
      if (!cartids) {
        dispatch('showToast', { message: '購物車ID不能為空', type: 'warning' })
        return null
      }

      try {
        const res = await goodsApi.toPay({
          cartids,
          expresstype: payload.expresstype,
          name: payload.name,
          phone: payload.phone,
          address: payload.address,
          envtype: 2,
          remark: payload.remark || '',
          isUSDTpay: payload.isUSDTpay || 0,
          ischinapay: payload.ischinapay || 0,
          hkWallet: payload.hkWallet || 'false',
          coupon_id: payload.coupon_id || ''
        })

        if (res.code === 1) {
          const payUrl = (res.data && res.data.paramMap && res.data.paramMap.payUrl) || ''
          const bianhao = (res.data && res.data.bianhao) || ''
          commit('ADD_ORDER', {
            id: payload.orderNumber,
            orderNumber: payload.orderNumber,
            date: new Date().toISOString(),
            status: 'unpaid',
            total: payload.total,
            payUrl
          })
          return [payUrl, bianhao]
        }

        dispatch('showToast', { message: res.msg || '下單失敗', type: 'error' })
        return null
      } catch {
        dispatch('showToast', { message: '下單失敗', type: 'error' })
        return null
      }
    },
    async saveAddress({ commit, dispatch, state }, form) {
      if (!state.user) return false
      const genderMap = { mr: 1, mrs: 2, ms: 3 }
      const apiParams = {
        gender: genderMap[form.salutation] || 1,
        surname: form.lastName || '',
        name: form.firstName || '',
        areano: form.phoneRegion || '',
        phone: form.phone || '',
        detailaddr: form.address || '',
        area: form.region || '',
        city: form.city || '',
        bianma: form.postalCode || '',
      }
      if (form.id) {
        apiParams.id = form.id
      }
      const res = await userApi.editAddress(apiParams)
      if (res.code === 1) {
        dispatch('showToast', { message: '地址已保存', type: 'success' })
        return true
      }
      dispatch('showToast', { message: res.msg || '保存失敗', type: 'error' })
      return false
    },
    async deleteAddress({ commit, dispatch, state }, id) {
      if (!state.user) return
      const res = await userApi.deleteAddress({ id })
      if (res.code === 1) {
        dispatch('showToast', { message: '地址已刪除', type: 'success' })
      } else {
        dispatch('showToast', { message: res.msg || '刪除失敗', type: 'error' })
      }
    },
    async logout({ commit }) {
      try { await userApi.logout() } catch (e) { /* ignore */ }
      commit('SET_USER', null)
      commit('CLEAR_NOTIFICATIONS')
      commit('SET_ACCOUNT_MODAL_VISIBLE', false)
    },
    async fetchUserInfo({ commit, state }) {
      if (!state.user) return null
      const res = await userApi.getInfo()
      if (res.code === 1 && res.data) {
        const user = { ...state.user, ...res.data }
        commit('SET_USER', user)
        return user
      }
      return null
    },
    async fetchCoupons({ state }, params = {}) {
      if (!state.user) return []
      const res = await userApi.getCouponList({ status: params.status })
      if (res.code === 1 && res.data && Array.isArray(res.data.list)) {
        return res.data.list.map(c => ({
          id: c.id,
          couponId: c.coupon_id,
          title: c.coupon_name,
          titleEn: c.coupon_name_en,
          value: c.coupon_money,
          minSpend: parseFloat(c.coupon_basepay) || 0,
          coupon_type: String(c.coupon_type || '1'),
          brand_ids: c.brand_ids || '',
          goods_ids: c.goods_ids || '',
          goodsinfo: c.goodsinfo || [],
          brandinfo: c.brandinfo || [],
          status: c.status === '0' ? 'available' : (c.status === '1' ? 'used' : 'expired'),
          apiStatus: c.status,
          expiryDate: c.expiretime ? new Date(c.expiretime * 1000).toISOString() : ''
        }))
      }
      return []
    },
    async fetchReceipts({ state }) {
      if (!state.user) return []
      const res = await userApi.getStoreBuyLog()
      if (res.code === 1 && res.data && res.data.list) return res.data.list
      return []
    },
    async fetchAddresses({ state }) {
      if (!state.user) return []
      const res = await userApi.getAddressList()
      if (res.code === 1 && res.data) return res.data
      return []
    },
    async fetchOrders({ commit, state }, params = {}) {
      if (!state.user) return { list: [], totalCount: 0 }
      const res = await goodsApi.getOrderList({
        page: params.page || 1,
        count: params.count || 20,
        status: params.status
      })
      if (res.code === 1 && res.data && res.data.list) {
        commit('SET_API_ORDERS', res.data.list)
        return { list: res.data.list, totalCount: res.data.total_count || 0 }
      }
      return { list: [], totalCount: 0 }
    },
    async fetchOrderDetail({ state }, orderId) {
      if (!state.user) return null
      const res = await goodsApi.getOrderDetail({ id: orderId })
      if (res.code === 1 && res.data) return res.data
      return null
    },
    async fetchScoreGoods({ state }, params = {}) {
      if (!state.user) return { list: [], totalCount: 0 }
      const res = await scoreshopApi.getScoreGoodsList({
        page: params.page || 1,
        count: params.count || 20
      })
      if (res.code === 1 && res.data && res.data.list) return { list: res.data.list, totalCount: res.data.count || 0 }
      return { list: [], totalCount: 0 }
    },
    async fetchScoreOrders({ state }, params = {}) {
      if (!state.user) return { list: [], totalCount: 0 }
      const res = await scoreshopApi.getScoreOrderList({
        page: params.page || 1,
        count: params.count || 10
      })
      if (res.code === 1 && res.data && res.data.list) return { list: res.data.list, totalCount: res.data.count || 0 }
      return { list: [], totalCount: 0 }
    },
    async fetchScoreCoupons({ state }, params = {}) {
      if (!state.user) return { list: [], totalCount: 0 }
      const res = await scoreshopApi.getCouponList({
        page: params.page || 1,
        count: params.count || 20
      })
      if (res.code === 1 && res.data && res.data.list) return { list: res.data.list, totalCount: res.data.count || 0 }
      return { list: [], totalCount: 0 }
    },
    async fetchFreeCoupons({ state }, params = {}) {
      if (!state.user) return { list: [], totalCount: 0 }
      const res = await scoreshopApi.getFreeList({
        page: params.page || 1,
        count: params.count || 20
      })
      if (res.code === 1 && res.data) {
        // 兼容两种格式: { list: [...] } 或直接返回数组
        const list = Array.isArray(res.data) ? res.data : (res.data.list || [])
        const count = res.data.count || res.data.total_count || list.length
        return { list, totalCount: count }
      }
      return { list: [], totalCount: 0 }
    },
    async exchangeGoods({ dispatch, state }, goodsId) {
      if (!state.user) return false
      const res = await scoreshopApi.exchangeGoods({ goods_id: goodsId })
      if (res.code === 1) {
        dispatch('showToast', { message: '兌換成功', type: 'success' })
        return true
      }
      dispatch('showToast', { message: res.msg || '兌換失敗', type: 'error' })
      return false
    },
    async exchangeCoupon({ dispatch, state }, couponId) {
      if (!state.user) return false
      const res = await scoreshopApi.exchangeCoupon({ coupon_id: couponId })
      if (res.code === 1) {
        dispatch('showToast', { message: '兌換成功', type: 'success' })
        return true
      }
      dispatch('showToast', { message: res.msg || '兌換失敗', type: 'error' })
      return false
    }
  }
})
